import { ChangeDetectionStrategy, Component, Input, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateService } from '@ngx-translate/core';

import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import { 
  PosShiftOpenFormConfig,
  getPosShiftOpenFormConfig
} from '../../pos-shift-open.config';
import { PosShiftOpenService, PosShiftOpening } from '../../services/pos-shift-open.service';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';

@Component({
  selector: 'app-pos-shift-open-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    AddFormComponent
  ],
  providers: [MessageService],
  templateUrl: './pos-shift-open-form-wrapper.component.html',
  styleUrls: ['./pos-shift-open-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosShiftOpenFormWrapperComponent implements OnInit {
  @Input() config!: PosShiftOpenFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  
  // Form data management
  formData = signal<any>({});
  originalApiData = signal<any>(null); // Store original data from API
  
  // Loading state
  initialLoading = signal(true);
  
  // Dropdown data signals
  posOptions = signal<Array<{ label: string; value: number }>>([]);
  shopOptions = signal<Array<{ label: string; value: number }>>([]);
  cashierOptions = signal<Array<{ label: string; value: number }>>([]);

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    const baseData = {
      ...this.formData()
    };
    
    return baseData;
  });

  // Computed property to get form fields with translated default values
  translatedFormFields = computed(() => {
    return this.config.formFields.map(field => ({
      ...field,
      label: this.translateService.instant(field.label),
      placeholder: field.placeholder ? this.translateService.instant(field.placeholder) : ''
    }));
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private translateService: TranslateService,
    private posShiftOpenService: PosShiftOpenService
  ) {}

  ngOnInit(): void {
    // Load data for view mode only
    if (this.viewMode) {
      this.loadShiftOpeningData();
    } else {
      this.initialLoading.set(false);
    }
  }


  /**
   * Load shift opening data for view mode
   */
  private loadShiftOpeningData(): void {
    const shiftId = this.route.snapshot.params['id'];
    
    if (shiftId) {
      this.posShiftOpenService.getPosShiftOpeningById(Number(shiftId)).subscribe({
        next: (shiftOpening: PosShiftOpening | null) => {
          if (shiftOpening) {
            this.originalApiData.set(shiftOpening);
            this.formData.set({
              shift_id: shiftOpening.shift_id,
              posCode: shiftOpening.posCode,
              shopName: shiftOpening.shopName,
              shopAddress: shiftOpening.shopAddress,
              cashierName: shiftOpening.cashierName,
              shiftDate: shiftOpening.shiftDate,
              openingTime: this.formatDateTime(shiftOpening.openingTime),
              closingTime: this.formatDateTime(shiftOpening.closingTime),
              openingAmount: shiftOpening.openingAmount,
              closingAmount: shiftOpening.closingAmount,
              status: shiftOpening.status,
              createdBy: shiftOpening.createdBy,
              creationDate: shiftOpening.creationDate
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant('pos.shift_open.messages.not_found')
            });
            this.router.navigate([this.config.listRoute]);
          }
          this.initialLoading.set(false);
        },
        error: (error: any) => {
          console.error('Error loading shift opening:', error);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('pos.shift_open.messages.load_failed')
          });
          this.router.navigate([this.config.listRoute]);
        }
      });
    } else {
      console.error('No shift ID found in route parameters');
      this.router.navigate([this.config.listRoute]);
    }
  }

  /**
   * Handle form value changes
   */
  onFormValueChanges(value: any): void {
    this.formData.set(value);
  }

  /**
   * Handle form actions (back only for view mode)
   */
  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'back':
        this.router.navigate([this.config.listRoute]);
        break;
      default:
        console.warn('Unknown form action:', event.action);
    }
  }

  formatDateTime = formatDateTime;
}
